import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useWebSocketManager } from './hooks/useWebSocketManager';
import { RegionColumn } from './components';
import type { AWSRegion } from './types';
import './App.css';

/**
 * Error Boundary Component
 * Catches rendering errors and displays fallback UI
 * Requirements: 7.3
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p>An error occurred while rendering the application.</p>
          <details>
            <summary>Error details</summary>
            <pre>{this.state.error?.message}</pre>
          </details>
          <button onClick={() => window.location.reload()}>
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Main App Component
 * Integrates multi-region WebSocket Manager with three-column layout
 * Requirements: 4.1, 4.2, 4.5, 5.1, 5.2, 7.3
 */
function App() {
  // Get WebSocket URLs from environment variables for all three regions
  const websocketUrls = {
    'eu-west-1': import.meta.env.VITE_WEBSOCKET_URL_EU_WEST_1 || 'ws://localhost:3001',
    'ap-south-1': import.meta.env.VITE_WEBSOCKET_URL_AP_SOUTH_1 || 'ws://localhost:3002',
    'us-east-1': import.meta.env.VITE_WEBSOCKET_URL_US_EAST_1 || 'ws://localhost:3003',
  };

  // Use multi-region WebSocket Manager hook
  const {
    connectionStatus,
    events,
    connect,
  } = useWebSocketManager({
    urls: websocketUrls,
    autoConnect: true,
  });

  // Define regions in order
  const regions: AWSRegion[] = ['eu-west-1', 'ap-south-1', 'us-east-1'];

  return (
    <ErrorBoundary>
      <div className="app">
        <div className="app-columns">
          {regions.map((region) => (
            <RegionColumn
              key={region}
              region={region}
              events={events[region]}
              connectionStatus={connectionStatus[region]}
              onReconnect={() => connect(region)}
            />
          ))}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
